import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  Moon,
  Smartphone,
  ShieldCheck,
  Sun,
  UserRound
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { consumePostAuthPath, persistPostAuthPath, sanitizePostAuthPath } from '../utils/postAuthNavigation';
import { useTheme } from '../contexts/ThemeContext';
import {
  persistMunicipioId,
  resolveMunicipioContext,
  wasLegacyShortcutRecovered,
} from '../utils/municipioContext';

const DEFAULT_LOGO_URL = 'https://firebasestorage.googleapis.com/v0/b/portal-sisfol.firebasestorage.app/o/LOGOSISRH%2FSERVIDOR%2FSISRH%20-%20servidor.png?alt=media&token=517262c7-d4ea-4d02-b0f0-2799e6158542';

const DEMO_ACCESS_KEY = 'sisfol2026';
const DEMO_SERVER_CPF = '01397414456';
const DEMO_SERVER_PASSWORD = '123456';

const onlyCpfDigits = (value) => String(value || '').replace(/\D/g, '').slice(0, 11);

const formatCpf = (value) => {
  const digits = onlyCpfDigits(value);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
};

const getFirstName = (name) => {
  const cleanName = String(name || '').trim();
  return cleanName ? cleanName.split(' ')[0] : '';
};

const normalizePortalDescription = (value) => String(value || '').trim();

const isLegacyPortalDescription = (value) => {
  const normalized = normalizePortalDescription(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  return normalized.includes('recadastramento') || normalized.includes('prova de vida');
};

const AccessOverlay = ({ success, welcomeName }) => (
  <div className="fixed inset-0 z-[9999] flex cursor-wait items-center justify-center bg-white/90 p-4 backdrop-blur-xl dark:bg-gray-950/90">
    <div className="w-full max-w-md rounded-[2rem] border border-white/70 bg-white/95 p-7 text-center shadow-2xl dark:border-gray-800 dark:bg-gray-900/95 sm:p-8">
      <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center">
        {success ? (
          <div className="relative flex h-24 w-24 items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" />
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-emerald-50 shadow-inner dark:bg-emerald-900/30">
              <CheckCircle2 size={56} className="text-emerald-500 dark:text-emerald-300" />
            </div>
          </div>
        ) : (
          <div className="relative flex h-24 w-24 items-center justify-center">
            <div className="absolute inset-0 rounded-full border-4 border-blue-500/10" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin dark:border-t-blue-300" />
            <div className="absolute inset-3 rounded-full bg-blue-50 shadow-inner dark:bg-blue-900/30" />
            <ShieldCheck size={38} className="relative z-10 text-blue-600 dark:text-blue-300" />
          </div>
        )}
      </div>

      <p className="mb-2 text-xs font-black uppercase tracking-[0.24em] text-blue-600 dark:text-blue-300">
        Portal do Servidor
      </p>

      <h2 className={`text-2xl font-black tracking-tight ${success ? 'text-emerald-600 dark:text-emerald-300' : 'text-gray-900 dark:text-white'}`}>
        {success ? `Olá${welcomeName ? `, ${welcomeName}` : ''}.` : 'Preparando seu acesso...'}
      </h2>

      <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {success
          ? 'Acesso autorizado. Carregando suas informações.'
          : 'Conferindo CPF, senha e vínculo funcional.'}
      </p>

      <div className="mt-6 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div className="h-full w-1/2 rounded-full bg-blue-600 sisfol-loading-bar dark:bg-blue-300" />
      </div>
    </div>
  </div>
);

const AuthPage = () => {
  const [cpfDigits, setCpfDigits] = useState('');
  const [municipioId, setMunicipioId] = useState(null);
  const [portalDescription, setPortalDescription] = useState('');
  const [municipioName, setMunicipioName] = useState('');
  const [municipioLogoUrl, setMunicipioLogoUrl] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localMessage, setLocalMessage] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [welcomeName, setWelcomeName] = useState('');

  const passwordRef = useRef(null);
  const e2eAutoLoginStartedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const {
    login,
    isAuthenticated,
    user,
    vinculos,
    vinculoSelecionado,
    authError,
    clearAuthError
  } = useAuth();

  const logoutMessage = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('message');
  }, [location.search]);

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return sanitizePostAuthPath(params.get('returnTo'), '');
  }, [location.search]);

  useEffect(() => {
    if (returnTo) persistPostAuthPath(returnTo);
  }, [returnTo]);

  useEffect(() => {
    const { municipioId: selectedMunicipioId } = resolveMunicipioContext({ search: location.search });

    if (selectedMunicipioId) {
      setMunicipioId(selectedMunicipioId);
      persistMunicipioId(selectedMunicipioId);
    } else {
      setMunicipioId(null);
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const demoKey = params.get('demoKey');
    const demo = params.get('demo');

    if (demoKey !== DEMO_ACCESS_KEY || demo !== 'servidor') return;

    setCpfDigits(onlyCpfDigits(DEMO_SERVER_CPF));

    if (passwordRef.current) {
      passwordRef.current.value = DEMO_SERVER_PASSWORD;
    }

    setLocalMessage('');

    if (clearAuthError) {
      clearAuthError();
    }
  }, [location.search, clearAuthError]);

  useEffect(() => {
    if (!municipioId) return undefined;

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        const [{ doc, getDoc }, firebaseModule] = await Promise.all([
          import('firebase/firestore'),
          import('../services/firebase')
        ]);

        if (cancelled) return;

        const municipioRef = doc(
          firebaseModule.db,
          `artifacts/${firebaseModule.firestorePathId}/public/data/municipalities/${municipioId}`
        );

        const municipioSnap = await getDoc(municipioRef);

        if (cancelled || !municipioSnap.exists()) return;

        const data = municipioSnap.data();
        const resolvedMunicipioName = data.nome || data.name || data.razaoSocial || '';
        setPortalDescription(data.portalDescription || '');
        setMunicipioName(resolvedMunicipioName);
        setMunicipioLogoUrl(data.logoUrl || '');

        // Reaproveita o nome já lido para a tela de login; não cria nova consulta no Dashboard.
        if (resolvedMunicipioName) {
          try {
            window.localStorage.setItem(`sisdp_server_municipio_name:${municipioId}`, resolvedMunicipioName);
          } catch (storageError) {
            // Preferência visual não pode bloquear autenticação.
          }
        }
      } catch (error) {
        console.warn('Dados visuais do município não foram carregados:', error);
      }
    }, 1200);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [municipioId]);

  useEffect(() => {
    if (authError) {
      setLocalMessage(authError);
    }
  }, [authError]);

  useEffect(() => {
    if (logoutMessage) {
      setLocalMessage(logoutMessage);
    }
  }, [logoutMessage]);

  useEffect(() => {
    if (isAuthenticated && !logoutMessage && !isLoggingIn) {
      if (user?.mustChangePassword) {
        navigate('/change-password', {replace: true});
        return;
      }
      if (Array.isArray(vinculos) && vinculos.length > 1 && !vinculoSelecionado) {
        navigate('/selecionar-vinculo', {replace: true});
        return;
      }
      navigate(consumePostAuthPath('/dashboard'), { replace: true });
    }
  }, [isAuthenticated, logoutMessage, isLoggingIn, navigate, user?.mustChangePassword, vinculoSelecionado, vinculos]);

  const handleCpfChange = (event) => {
    setCpfDigits(onlyCpfDigits(event.target.value));
  };

  const handleSubmit = async (event, credentialsOverride = null) => {
    event.preventDefault();

    if (isLoggingIn) return;

    if (clearAuthError) clearAuthError();
    setLocalMessage('');

    const cpfForLogin = credentialsOverride?.cpf
      ? onlyCpfDigits(credentialsOverride.cpf)
      : cpfDigits;
    const password = credentialsOverride?.password
      || (passwordRef.current ? passwordRef.current.value : '');

    if (!municipioId) {
      setLocalMessage('Município não identificado. Abra o link correto enviado pelo Departamento Pessoal.');
      return;
    }

    if (cpfForLogin.length !== 11) {
      setLocalMessage('Digite os 11 números do CPF.');
      return;
    }

    if (!password) {
      setLocalMessage('Digite sua senha.');
      return;
    }

    setIsLoggingIn(true);
    setLoginSuccess(false);
    setWelcomeName('');

    try {
      const result = await login(cpfForLogin, password, municipioId);

      if (!result || !result.success) {
        setLocalMessage(result?.message || 'Não foi possível entrar. Confira CPF e senha.');
        setIsLoggingIn(false);
        return;
      }

      const name = result?.userData?.name || result?.userData?.displayName || result?.userData?.nome || '';
      setWelcomeName(getFirstName(name));
      setLoginSuccess(true);

      window.setTimeout(() => {
        if (result.needsVinculoSelection) {
          navigate('/selecionar-vinculo', { replace: true });
          return;
        }

        if (result.mustChangePassword) {
          navigate('/change-password', { replace: true });
          return;
        }

        navigate(consumePostAuthPath('/dashboard'), { replace: true });
      }, 450);
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      setLocalMessage('Erro ao tentar entrar. Tente novamente.');
      setIsLoggingIn(false);
      setLoginSuccess(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isLocalE2E = process.env.REACT_APP_SISDP_ENVIRONMENT === 'E2E'
      && process.env.REACT_APP_E2E_USE_EMULATORS === 'true';
    if (!isLocalE2E || params.get('e2eAutoLogin') !== 'true'
        || !municipioId || e2eAutoLoginStartedRef.current) return;

    const cpf = onlyCpfDigits(process.env.REACT_APP_E2E_SERVER_CPF);
    const password = String(process.env.REACT_APP_E2E_SERVER_PASSWORD || '');
    if (cpf.length !== 11 || !password) return;

    e2eAutoLoginStartedRef.current = true;
    setCpfDigits(cpf);
    if (passwordRef.current) passwordRef.current.value = password;
    void handleSubmit({ preventDefault: () => {} }, { cpf, password });
    // Este gatilho existe somente no laboratório local e deve executar uma vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, municipioId]);

  const handleCloseMessage = () => {
    setLocalMessage('');
    if (clearAuthError) clearAuthError();

    if (logoutMessage) {
      navigate('/login', { replace: true });
    }
  };

  const isBlocked = isLoggingIn || loginSuccess;
  const logoUrl = municipioLogoUrl || DEFAULT_LOGO_URL;
  const municipalPortalDescription = normalizePortalDescription(portalDescription);
  const showMunicipalPortalDescription = Boolean(municipalPortalDescription) && !isLegacyPortalDescription(municipalPortalDescription);

  return (
    <div className="server-auth-shell server-auth-approved relative min-h-screen min-h-[100dvh] overflow-hidden bg-[var(--sisdp-color-canvas)] text-[var(--sisdp-color-text)]">
      {isBlocked && <AccessOverlay success={loginSuccess} welcomeName={welcomeName} />}

      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute -right-28 top-1/3 h-80 w-80 rounded-full bg-cyan-500/10 blur-3xl" />
      </div>

      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-end gap-2 px-4 pb-1 pt-[max(1rem,env(safe-area-inset-top))] sm:px-6">
        <Link
          to="/instalar"
          className="hidden min-h-11 items-center gap-2 rounded-xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] px-3 text-sm font-bold text-gray-600 shadow-sm transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800 sm:inline-flex"
        >
          <Smartphone size={18} aria-hidden="true" />
          Usar no celular
        </Link>
        <button
          type="button"
          onClick={toggleTheme}
          className="server-touch-target inline-flex items-center justify-center rounded-xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] text-gray-600 shadow-sm transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
          aria-label={theme === 'light' ? 'Ativar tema escuro' : 'Ativar tema claro'}
        >
          {theme === 'light' ? <Moon size={19} /> : <Sun size={19} />}
        </button>
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-2xl flex-col items-center px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-2 sm:px-6 sm:pt-5 lg:min-h-[calc(100dvh-5rem)] lg:justify-center lg:py-8">
        <section className="mb-6 w-full max-w-lg text-center sm:mb-7">
          <div className="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded-[1.6rem] border border-[var(--sisdp-color-border)] bg-white p-2.5 shadow-sm dark:bg-gray-900 sm:h-24 sm:w-24">
            <img
              src={logoUrl}
              alt="Logo do Portal do Servidor"
              className="max-h-full max-w-full object-contain"
              onError={(event) => {
                event.currentTarget.src = DEFAULT_LOGO_URL;
              }}
            />
          </div>

          <p className="mt-4 text-xs font-black uppercase tracking-[0.2em] text-blue-600 dark:text-blue-300">SISDP</p>
          <h1 className="mt-1.5 text-[2rem] font-extrabold tracking-[-0.025em] sm:text-[2.35rem] text-gray-950 dark:text-white">Portal do Servidor</h1>
          <p className="mx-auto mt-2 max-w-lg text-base leading-relaxed text-gray-600 dark:text-gray-300">
            Acesse seus serviços com segurança e praticidade.
          </p>

          {municipioName && (
            <div className="mx-auto mt-4 inline-flex min-h-11 max-w-full items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-bold text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200">
              <Building2 size={18} className="shrink-0" aria-hidden="true" />
              <span className="truncate">{municipioName}</span>
            </div>
          )}
        </section>

        <section className="w-full max-w-lg">
          <div className="server-auth-card rounded-[1.75rem] border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] p-5 shadow-[var(--sisdp-shadow-lg)] sm:p-7">
            <div className="mb-6">
              <h2 className="text-2xl font-extrabold tracking-tight text-gray-950 dark:text-white">Acesse sua conta</h2>
              <p className="mt-2 text-sm leading-relaxed text-[var(--sisdp-color-text-muted)]">
                Informe seu CPF e sua senha para continuar.
              </p>
              {showMunicipalPortalDescription && (
                <p className="mt-3 rounded-xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface-subtle)] px-3 py-2 text-xs leading-relaxed text-[var(--sisdp-color-text-muted)]">
                  <span className="font-black text-[var(--sisdp-color-text)]">Orientação do município:</span> {municipalPortalDescription}
                </p>
              )}
            </div>

            {!municipioId && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-200" role="alert">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <p>Município não identificado. Abra o link correto enviado pelo Departamento Pessoal.</p>
              </div>
            )}

            {municipioId && wasLegacyShortcutRecovered() && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-relaxed text-blue-800 dark:border-blue-800/60 dark:bg-blue-900/20 dark:text-blue-200" role="status">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <p>Este atalho antigo ainda permite acesso, mas precisa ser reinstalado. Remova o ícone antigo, abra novamente o link oficial enviado pelo Departamento Pessoal e use “Adicionar à Tela de Início”.</p>
              </div>
            )}

            {localMessage && (
              <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-200" role="alert">
                <p>{localMessage}</p>
                <button type="button" onClick={handleCloseMessage} className="mt-2 min-h-11 text-sm font-black underline underline-offset-4">
                  Fechar aviso
                </button>
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit} noValidate={false}>
              <div>
                <label htmlFor="cpf" className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-200">CPF</label>
                <div className="relative">
                  <UserRound className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    id="cpf"
                    type="text"
                    inputMode="numeric"
                    autoComplete="username"
                    value={formatCpf(cpfDigits)}
                    onChange={handleCpfChange}
                    placeholder="000.000.000-00"
                    required
                    disabled={isBlocked}
                    className="min-h-14 w-full rounded-2xl border border-gray-300 bg-white py-3 pl-12 pr-4 text-base text-gray-900 shadow-sm outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
                  />
                </div>
                <p className="mt-1.5 text-xs text-[var(--sisdp-color-text-muted)]">Digite os 11 números do seu CPF.</p>
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-200">Senha</label>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    ref={passwordRef}
                    placeholder="Digite sua senha"
                    required
                    disabled={isBlocked}
                    className="min-h-14 w-full rounded-2xl border border-gray-300 bg-white py-3 pl-12 pr-14 text-base text-gray-900 shadow-sm outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="server-touch-target absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    disabled={isBlocked}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isBlocked || !municipioId}
                className="flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-base font-black text-white shadow-lg shadow-blue-600/20 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none dark:bg-blue-500 dark:hover:bg-blue-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
              >
                <ShieldCheck size={19} aria-hidden="true" />
                {isLoggingIn ? 'Preparando acesso...' : 'Entrar'}
              </button>
            </form>

            <div className="mt-6 border-t border-[var(--sisdp-color-border)] pt-5">
              <Link
                to="/instalar"
                className="flex min-h-12 w-full items-center justify-between rounded-2xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface-subtle)] px-4 text-sm font-bold text-blue-700 transition-colors hover:bg-blue-50 dark:text-blue-200 dark:hover:bg-blue-950/30 sm:hidden"
              >
                <span className="inline-flex items-center gap-2"><Smartphone size={18} /> Usar como aplicativo</span>
                <span aria-hidden="true">›</span>
              </Link>
              <p className="mt-4 text-center text-xs leading-relaxed text-[var(--sisdp-color-text-muted)]">
                Nunca informe sua senha fora deste portal. Em caso de dúvida, procure o Departamento Pessoal do município.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AuthPage;
